import { Component, inject, signal, computed, ViewChild, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { RouterOutlet, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { LoadingService } from './services/loading.service';
import { AuthService } from './services/auth.service';
import { UpdateService } from './services/update';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule, MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';
import { map, filter, switchMap, startWith, pairwise } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { HeaderComponent } from './header/header';
import { NavComponent } from './nav/nav';
import { TripDialogComponent } from './pages/my-trips/trip-dialog';
import { ChangelogDialogComponent } from './changelog-dialog/changelog-dialog.component';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { toSignal } from '@angular/core/rxjs-interop';
import packageInfo from '../../package.json';

@Component({
  selector: 'app-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterOutlet,
    MatProgressSpinnerModule,
    MatSidenavModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    HeaderComponent,
    NavComponent
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  animations: [
    trigger('sidebarAnimation', [
      state('collapsed', style({
        width: '60px'
      })),
      state('expanded', style({
        width: '250px'
      })),
      transition('collapsed <=> expanded', [
        animate('0.3s ease-in-out')
      ])
    ])
  ]
})
export class App implements OnInit, OnDestroy {
  public readonly loadingService = inject(LoadingService);
  private readonly authService = inject(AuthService);
  private readonly updateService = inject(UpdateService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  public readonly appVersion = packageInfo.version;

  @ViewChild('sidenav') sidenav!: MatSidenav;
  @ViewChild(MatSidenavContainer) sidenavContainer?: MatSidenavContainer;

  protected readonly user$ = this.authService.userData$;
  protected readonly isAuthenticated$ = this.authService.isAuthenticated$;
  protected readonly isAuthReady$ = this.authService.isAuthReady$;

  private authSubscription?: Subscription;

  isHandset = toSignal(
    this.breakpointObserver
      .observe('(max-width: 799.98px)')
      .pipe(map((result) => result.matches)),
    { initialValue: this.breakpointObserver.isMatched('(max-width: 799.98px)') }
  );

  isMediumScreen = toSignal(
    this.breakpointObserver
      .observe('(min-width: 800px) and (max-width: 1279.98px)')
      .pipe(map((result) => result.matches)),
    { initialValue: this.breakpointObserver.isMatched('(min-width: 800px) and (max-width: 1279.98px)') }
  );

  isLargeScreen = toSignal(
    this.breakpointObserver
      .observe(['(min-width: 1280px)'])
      .pipe(map((result) => result.matches)),
    { initialValue: this.breakpointObserver.isMatched('(min-width: 1280px)') }
  );

  isSidenavExpanded = signal(false);

  onSidebarAnimationDone() {
    this.sidenavContainer?.updateContentMargins();
  }

  ngOnInit() {
    this.authSubscription = this.isAuthReady$.pipe(
      filter(ready => ready),
      switchMap(() => this.isAuthenticated$),
      startWith(null as boolean | null),
      pairwise(),
      filter(([prev, curr]) => prev === true && curr === false)
    ).subscribe(() => {
      this.router.navigate(['/login']);
    });
  }

  ngOnDestroy() {
    this.authSubscription?.unsubscribe();
  }

  toggleSidenav() {
    this.sidenav.toggle();
  }

  onSidenavMouseEnter() {
    if (this.isMediumScreen()) {
      this.isSidenavExpanded.set(true);
    }
  }

  onSidenavMouseLeave() {
    if (this.isMediumScreen()) {
      this.isSidenavExpanded.set(false);
    }
  }

  sidebarState = computed(() => {
    if (this.isLargeScreen()) {
      return 'expanded';
    }
    return this.isSidenavExpanded() ? 'expanded' : 'collapsed';
  });

  isIconOnly = computed(() => {
    return this.isMediumScreen() && !this.isSidenavExpanded();
  });

  addTrip() {
    this.dialog.open(TripDialogComponent, {
      width: '80vw',
      maxWidth: '800px',
      data: {}
    });
  }

  openChangelog() {
    this.dialog.open(ChangelogDialogComponent, {
      width: '550px',
      maxWidth: '90vw',
      disableClose: false
    });
  }
}
